import React, { memo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { Dropdown, SlideUp, Text, Icon } from 'common-components'
import { useDropdownState } from 'hooks/useDropdownState'
import { useResponsive } from 'hooks/useResponsive'
import { LINE_HEIGHT_OFFSET, SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { editorThunks } from 'store/features/editor/thunks'
import { dispatch } from 'store'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import ThunderIcon from 'assets/svgr-icons/Thunder.svg'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { useEventListener } from 'hooks'
import { useScrollableElement } from '../../../context'
import { IOption } from '../OptionDropdown/types'
import ImageAspectRatioDropdownContent from './ImageAspectRatioDropdownContent'
import * as Styled from './styled'
import { IMAGE_AR_OVERLAY_CLASS } from './styled'

interface IImageAspectRatioDropdownProps {
  options: Array<IOption>
  selectedValue: string | null
  onSelect: (value: string | null) => void
  className?: string
}

const ImageAspectRatioDropdown: React.FC<IImageAspectRatioDropdownProps> = ({
  options,
  selectedValue,
  onSelect,
  className
}) => {
  const { scrollableElementRef } = useScrollableElement()
  const { isOpen, closeDropdown, toggleDropdown } = useDropdownState(
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(true)),
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(false))
  )

  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const refImageSizeCreditsCost = useSelector(
    aiImageSelectors.refImageSizeCreditsCost
  )
  const resizeCountByRatio = useSelector(aiVideoSelectors.resizeCountByRatio)

  const handleSelect = useCallback(
    (value: string | null) => {
      onSelect(value)
      closeDropdown()
    },
    [onSelect, closeDropdown]
  )

  useEventListener(
    'scroll',
    closeDropdown,
    !isTablet ? scrollableElementRef.current : null,
    true
  )

  const isOriginalSelected = selectedValue === null
  const selectedOption = options.find(option => option.value === selectedValue)
  const selectedResizeCount = selectedValue
    ? (resizeCountByRatio[selectedValue]?.resizeFrameCount ?? 0)
    : 0

  const headerContent =
    isOriginalSelected || selectedResizeCount === 0 ? (
      <Styled.HeaderContent>
        <Text
          type="h2"
          color="secondaryDark"
          ellipsis
          lineHeight={LINE_HEIGHT_OFFSET}
        >
          {isOriginalSelected
            ? translate(T.ORIGINAL_LABEL)
            : selectedOption?.label}
        </Text>
      </Styled.HeaderContent>
    ) : (
      <Styled.HeaderContent>
        <Text type="h2" color="secondaryDark" ellipsis>
          {selectedOption?.label}
        </Text>
        <Icon icon={<ThunderIcon />} size="small" color="secondary" />
      </Styled.HeaderContent>
    )

  const dropdownContent = (
    <ImageAspectRatioDropdownContent
      options={options}
      selectedValue={selectedValue}
      onSelect={handleSelect}
      creditsCost={refImageSizeCreditsCost}
      resizeCountByRatio={resizeCountByRatio}
    />
  )

  if (isTablet && isOpen) {
    return createPortal(
      <SlideUp isOpen={isOpen} onClose={closeDropdown}>
        {dropdownContent}
      </SlideUp>,
      document.body
    )
  }

  return (
    <>
      <Styled.ImageAspectRatioOverlayStyles />
      <Dropdown
        className={className}
        headerContent={headerContent}
        mainContent={dropdownContent}
        isOpen={isOpen}
        toggleDropdown={toggleDropdown}
        closeDropdown={closeDropdown}
        overlayClassName={IMAGE_AR_OVERLAY_CLASS}
      />
    </>
  )
}

export default memo(ImageAspectRatioDropdown)
